import {
  type CropData,
  type MediaLibraryFile,
  type MediaLibraryImage,
} from "./types-backend";
export type {
  CropData,
  MediaConvertData,
  MediaConverterResponse,
  MediaLibraryFile,
  MediaLibraryImage,
} from "./types-backend";

/** This is the main type describing what user wishes to upload, how it is loaded and what metadata should be attached */
export type UploadedFile = {
  file:
    | {
        type: "image";
        /** Filename without extension */
        name: string;
        /** File extension with dot, e.g. ".jpg" */
        ext: string;
        alt: string;
        decorative?: boolean;
        caption?: string;
        crop?: CropData;
      }
    | {
        type: "audio";
        name: string;
        ext: string;
        description?: Caption[];
        subtitles?: Caption[];
      }
    | {
        type: "video";
        name: string;
        ext: string;
        description?: Caption[];
        subtitles?: Caption[];
        audioDescription?: { _id: string; name: string; url: string; }
      }
    | { type: "document"; name: string; ext: string }
    | { type: "pdf"; name: string; ext: string }
    | { type: "epub"; name: string; ext: string }
    | { type: "spreadsheet"; name: string; ext: string }
    | { type: "presentation"; name: string; ext: string }
    | { type: "text"; content: string }
    | {
        type: "weblink";
        title: string;
        url: string;
        mode: "link" | "embed";
      };
  source:
    | {
        type: "library";
        id: string;
        url: string;
        /** added only for backward compat where we need access to full file metadata */
        raw?: MediaLibraryFile | MediaLibraryImage;
      }
    | { type: "disk"; file: File | null }
    | {
        type: "external_url";
        url: string;
        embeddable?: EmbedInfo;
      }
    | {
        type: "recording";
        file: File | null;
        /** url created via `URL.createObjectURL` */
        internalUrl: string | null;
      }
    // used for "weblink" and "text"
    | { type: "no-source" };
};

export const UploadedFile = {
  fileTypeList: (): Array<UploadedFileType> => [
    "audio",
    "document",
    "epub",
    "image",
    "pdf",
    "presentation",
    "spreadsheet",
    "text",
    "video",
    "weblink",
  ],

  defaultOfType: (type: UploadedFileType): UploadedFile => {
    const source: UploadedFile["source"] = { type: "disk", file: null };

    switch (type) {
      case "audio":
        return { source, file: { type, name: "", ext: "" } };
      case "image":
        return {
          source,
          file: { type, name: "", ext: "", alt: "", caption: "" },
        };
      case "video":
        return { source, file: { type, name: "", ext: "" } };
      case "document":
      case "presentation":
      case "spreadsheet":
        return { source, file: { type, name: "", ext: "" } };
      case "pdf":
      case "epub":
        return { source, file: { type, name: "", ext: "" } };
      case "text":
        return { source: { type: "no-source" }, file: { type, content: "" } };
      case "weblink":
        return {
          source: { type: "no-source" },
          file: { type, title: "", url: "", mode: "link" },
        };
    }
  },

  defaultSourceOfType: (
    type: UploadedFile["source"]["type"],
  ): UploadedFile["source"] => {
    switch (type) {
      case "library":
        return { type, id: "", url: "" };
      case "disk":
        return { type, file: null };
      case "external_url":
        return { type, url: "" };
      case "recording":
        return { type, file: null, internalUrl: null };
      case "no-source":
        return { type };
    }
  },

  isSourceConfigured: (upload: UploadedFile): boolean => {
    switch (upload.source.type) {
      case "library":
        return !!upload.source.id && !!upload.source.url;
      case "disk":
        return upload.source.file instanceof File;
      case "external_url":
        // url is a must, and for non-embedable links this is enough
        if (!upload.source.url) return false;
        if (!upload.source.embeddable) return !!upload.source.url;

        // specific sources may require more fields
        return (
          !!upload.source.embeddable.embedSrc &&
          !!upload.source.embeddable.serviceTag
        );
      case "recording":
        return (
          upload.source.file instanceof File && !!upload.source.internalUrl
        );
      case "no-source":
        return UploadedFile.isFileConfigured(upload);
    }
  },

  isFileConfigured: (upload: UploadedFile): boolean => {
    switch (upload.file.type) {
      case "image":
        return (
          !!upload.file.name.trim() &&
          !!upload.file.ext.trim() &&
          !!upload.file.crop &&
          (!!upload.file.decorative || !!upload.file.alt.trim())
        );
      case "audio":
      case "video":
      case "document":
      case "pdf":
      case "epub":
      case "presentation":
      case "spreadsheet":
        return !!upload.file.name.trim();
      case "text":
        return !!upload.file.content.trim();
      case "weblink":
        return !!upload.file.url.trim();
    }
  },

  validateFileFields: (
    upload: UploadedFile,
  ): Array<{ field: string; error: string }> => {
    // Backend Constrains on field length are defined in edrna_backend/files/models.py
    switch (upload.file.type) {
      case "image":
        return [
          upload.file.name.length + upload.file.ext.length > 255 && {
            field: "name",
            error: "Name is too long",
          },
          upload.file.alt.length > 512 && {
            field: "alt",
            error: "Alternate text is too long",
          },
          upload.file.caption &&
            upload.file.caption.length > 280 && {
              field: "caption",
              error: "Caption is too long",
            },
        ].filter(Boolean)
      case "audio":
      case "video":
      case "document":
      case "pdf":
      case "epub":
      case "presentation":
      case "spreadsheet":
        return [
          upload.file.name.length + upload.file.ext.length > 255 && {
            field: "name",
            error: "Name is too long",
          },
        ].filter(Boolean)
      case "text":
        return [
          upload.file.content.length === 0 && {
            field: "content",
            error: "Content must not be empty",
          },
        ].filter(Boolean)
      case "weblink":
        return [
          upload.file.url.length === 0 && {
            field: "url",
            error: "URL must not be empty",
          },
        ].filter(Boolean)
    }
  },

  allowedExtensions: (type: UploadedFileType): string[] => {
    return allowedExtensions[type];
  },

  sourceToUrl: (upload: UploadedFile): Promise<string> => {
    switch (upload.source.type) {
      case "external_url":
        return upload.source.embeddable
          ? Promise.resolve(upload.source.embeddable.embedSrc)
          : Promise.resolve(upload.source.url);
      case "library":
        return Promise.resolve(upload.source.url);
      case "disk":
        if (upload.source.file instanceof File) {
          return readFileAsDataUrl(upload.source.file);
        } else {
          return Promise.reject("No file selected");
        }
      case "recording":
        if (upload.source.internalUrl) {
          return Promise.resolve(upload.source.internalUrl);
        } else {
          return Promise.reject("No recording created");
        }
    }
  },

  sourceToEmbed: (
    upload: UploadedFile,
  ): Promise<{ tag: "video" | "audio" | "iframe"; src: string }> => {
    const defaultTag =
      upload.file.type === "video"
        ? "video"
        : upload.file.type === "audio"
          ? "audio"
          : "iframe";

    switch (upload.source.type) {
      case "external_url":
        return upload.source.embeddable
          ? Promise.resolve({
              tag: "iframe",
              src: upload.source.embeddable.embedSrc,
            })
          : Promise.resolve({ tag: defaultTag, src: upload.source.url });
      case "library":
        return Promise.resolve({ tag: defaultTag, src: upload.source.url });
      case "disk":
        if (upload.source.file instanceof File) {
          return readFileAsDataUrl(upload.source.file).then((src) => ({
            tag: defaultTag,
            src,
          }));
        } else {
          return Promise.reject("No file selected");
        }
      case "recording":
        if (upload.source.internalUrl) {
          return Promise.resolve({
            tag: defaultTag,
            src: upload.source.internalUrl,
          });
        } else {
          return Promise.reject("No recording created");
        }
    }
  },

  fileTypeLabels: {
    audio: "Audio",
    image: "Image",
    video: "Video",
    document: "Word Document",
    pdf: "PDF Document",
    epub: "EPUB Book",
    presentation: "Presentation",
    spreadsheet: "Spreadsheet",
    text: "Text",
    weblink: "Weblink",
  } satisfies Record<UploadedFileType, string>,

  fileTypeIcons: {
    audio: "graphic_eq",
    document: "docs",
    epub: "book_2",
    image: "imagesmode",
    pdf: "docs",
    presentation: "full_coverage",
    spreadsheet: "table",
    text: "code",
    video: "videocam",
    weblink: "link",
  } satisfies Record<UploadedFileType, string>,

  fromMediaLibraryItem: (
    item: MediaLibraryFile | MediaLibraryImage,
  ): UploadedFile["file"] | null => {
    const metadata = extractFileMetadataFromLibraryItem(item);

    if (!metadata) return null;

    const { type, name, ext } = metadata;

    switch (type) {
      case "image":
        return {
          type,
          name,
          ext,
          alt: "alt_text" in item ? item.alt_text || "" : "",
          caption: "caption" in item ? item.caption || "" : "",
          decorative: "decorative" in item ? item.decorative : undefined,
        };
      case "audio":
        return {
          type,
          name,
          ext,
          description:
            "description_array" in item
              ? item.description_array.map(Caption.parse).filter(Boolean)
              : [],
          subtitles:
            "subtitles_array" in item
              ? item.subtitles_array.map(Caption.parse).filter(Boolean)
              : [],
        };
      case "video":
        return {
          type,
          name,
          ext,
          description:
            "description_array" in item
              ? item.description_array.map(Caption.parse).filter(Boolean)
              : [],
          subtitles:
            "subtitles_array" in item
              ? item.subtitles_array.map(Caption.parse).filter(Boolean)
              : [],
          audioDescription: "audio_description" in item && item.audio_description ? item.audio_description : undefined,
        };
      case "document":
      case "pdf":
      case "epub":
      case "presentation":
      case "spreadsheet":
        return {
          type,
          name,
          ext,
        };
      // type "weblink" and "text" are not expected from library
      default:
        return null;
    }
  },

  serializeImageFields: (upload: UploadedFile): Partial<MediaLibraryImage> => ({
    ...(upload.file.type === "image" && {
        alt: upload.file.alt,
        alt_text: upload.file.alt,
        caption: upload.file.caption,
        decorative: upload.file.decorative,
      }),
  }),

  serializeCaptionsAndDescriptions: (upload: UploadedFile): Partial<MediaLibraryFile> => ({
    ...(upload.file.type === "video" &&
        upload.file.audioDescription?.url && {
            audio_description: upload.file.audioDescription,
        }),

    ...((upload.file.type === "video" || upload.file.type === "audio") && {
        description_array: (upload.file.description || [])
            .map(Caption.serializeToString)
            .filter(Boolean),
        subtitles_array: (upload.file.subtitles || [])
            .map(Caption.serializeToString)
            .filter(Boolean),
    }),
  })
};

export type UploadedFileType = UploadedFile["file"]["type"];

/** Utility type for narrowing TS inference, e.g. `UploadedFileOfType<'image'>` will allow accessing `file.caption` */
export type UploadedFileOfType<
  F extends UploadedFileType,
  S extends UploadedFile["source"]["type"] = UploadedFile["source"]["type"],
> = UploadedFile & { file: { type: F }; source: { type: S } };

/** For external urls pointing to services which allow embedding (e.g. youtube, vimeo) */
export type EmbedInfo = {
  /** url which can be used in <iframe src="..."> */
  embedSrc: string;
  /** e.g. "youtube" */
  serviceTag: string;
};

/** Frontend-only type describing subtitles/descriptions */
export type Caption = {
  language: string;
  name: string;
  dataUrl: string;
};

export const Caption = {
  parse: (rawCaption: string | null): Caption | null => {
    if (!rawCaption) return null;

    const chunks = rawCaption.split("::");

    if (chunks.length !== 3) return null;

    const [language, name, dataUrl] = chunks;

    return { language, name, dataUrl };
  },

  serializeToString: (caption: Caption): string => {
    return `${caption.language}::${caption.name}::${caption.dataUrl}`;
  },
};

export const mimeToExtension = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/svg+xml": ".svg",
  "image/webp": ".webp",

  // Video Formats
  "video/mp4": ".mp4",
  "video/quicktime": ".mov",
  "video/mpeg": ".mpg",
  "video/webm": ".webm",
  "video/x-m4v": ".m4v",

  // Audio Formats
  "audio/wav": ".wav",
  "audio/flac": ".flac",
  "audio/aac": ".aac",
  "audio/ogg": ".ogg",
  "audio/mpeg": ".mp3",
  "audio/mp4": ".m4a",

  // Document Types
  "application/pdf": ".pdf",
  "application/epub+zip": ".epub",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    ".docx",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/vnd.ms-powerpoint": ".ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    ".pptx",
  "text/plain": ".txt",
  "text/csv": ".csv",
  "application/rtf": ".rtf",
  "application/vnd.oasis.opendocument.text": ".odt",
  "application/vnd.oasis.opendocument.spreadsheet": ".ods",
  "application/vnd.oasis.opendocument.presentation": ".odp",

  // Archive Types
  "application/zip": ".zip",
  "application/x-rar-compressed": ".rar",
  "application/x-tar": ".tar",
  "application/x-7z-compressed": ".7z",

  // Other Types
  "application/javascript": ".js",
  "application/json": ".json",
  "text/html": ".html",
  "text/css": ".css",
  "application/xml": ".xml",
};

export const extensionToMime = Object.fromEntries(
  Object.entries(mimeToExtension)
    .map(([mime, ext]) => [ext, mime])
    .concat([
      [".jpeg", "image/jpeg"],
      [".mpeg", "video/mpeg"],
    ]),
);

export const mimeToFileType: Record<string, UploadedFile["file"]["type"]> = {
  // Images
  "image/jpeg": "image",
  "image/png": "image",
  "image/gif": "image",
  "image/svg+xml": "image",
  "image/webp": "image",

  // Videos
  "video/mp4": "video",
  "video/quicktime": "video",
  "video/mpeg": "video",
  "video/webm": "video",
  "video/x-m4v": "video",

  // Audios
  "audio/wav": "audio",
  "audio/flac": "audio",
  "audio/aac": "audio",
  "audio/ogg": "audio",
  "audio/mpeg": "audio",
  "audio/mp4": "audio",

  // Documents
  "application/pdf": "pdf",
  "application/epub+zip": "epub",

  "application/msword": "document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "document",
  "application/vnd.oasis.opendocument.text": "document",

  "application/vnd.ms-excel": "spreadsheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "spreadsheet",
  "application/vnd.oasis.opendocument.spreadsheet": "spreadsheet",

  "application/vnd.ms-powerpoint": "presentation",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "presentation",
  "application/vnd.oasis.opendocument.presentation": "presentation",
};

const allowedExtensions: Record<UploadedFileType, string[]> = {
  audio: [".mp3", ".m4a", ".ogg", ".wav"],
  image: [".jpg", ".jpeg", ".png", ".gif", ".webp"],
  video: [".mp4", ".mpg", ".mov", ".m4v", ".webm"],
  document: [".doc", ".docx"],
  presentation: [".ppt", ".pptx"],
  spreadsheet: [".xls", ".xlsx"],
  pdf: [".pdf"],
  epub: [".epub"],
  text: [""],
  weblink: [],
};

const extensionToType: Record<string, UploadedFileType> = Object.fromEntries(
  Object.entries(allowedExtensions).flatMap(([type, extensions]) =>
    extensions.map((ext) => [ext, type as UploadedFileType]),
  ),
);

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener("load", () => {
      resolve(reader.result.toString());
    });
    reader.addEventListener("error", reject);

    reader.readAsDataURL(file);
  });
}

export function extractFileExtension(filename: string): {
  name: string;
  ext: string;
} {
  const chunks = filename.split(".");
  const name = chunks.slice(0, -1).join(".") || filename;
  const ext = filename.includes(".") ? "." + chunks[chunks.length - 1] : "";

  return { name, ext };
}

export function extractFileExtensionFromUrl(urlText: string): {
  name: string;
  ext: string;
} | null {
  try {
    const url = new URL(urlText);
    const filename = url.pathname.split("/").pop();
    const { name, ext } = extractFileExtension(filename);

    return {
      name,
      ext,
    };
  } catch (err) {
    // exception thrown by URL constructor
    return null;
  }
}

export function extractFileMetadataFromLibraryItem(
  item: MediaLibraryFile | MediaLibraryImage,
): { type: UploadedFileType; ext: string; name: string } | null {
  const typeFromMime = item.mimetype && mimeToFileType[item.mimetype];
  const extFromMime = item.mimetype && mimeToExtension[item.mimetype];

  const { name, ext: extFromName } = extractFileExtension(item.name);
  const extFromUrl = extractFileExtensionFromUrl(item.url)?.ext;
  const ext = extFromMime || extFromName || extFromUrl;
  const type = typeFromMime || extensionToType[ext];

  return type ? { type, name, ext } : null;
}
